import type { ModalDefaultProps } from 'components/Modal';
import type { ModalType } from 'helpers/modals';
import { MODAL_LOADERS } from 'helpers/modals';
import React, { useCallback, useEffect, useRef } from 'react';

type ModalComponent = React.LazyExoticComponent<
  React.ComponentType<ModalDefaultProps>
>;

export const useModalsPreloading = (
  modals: ModalType[]
): Map<ModalType, ModalComponent> => {
  const cacheRef = useRef<Map<ModalType, ModalComponent>>(new Map());

  const preloadModals = useCallback((modals: ModalType[]) => {
    const cache = cacheRef.current;

    modals.forEach((modal) => {
      if (!cache.has(modal)) {
        try {
          const loader = MODAL_LOADERS[modal];
          if (!loader) {
            console.error(`No loader found for modal ${modal}`);
            return;
          }

          const LazyComponent = React.lazy(loader);
          cache.set(modal, LazyComponent);

          loader().catch((error) => {
            console.error(`Failed to preload modal ${modal}:`, error);
          });
        } catch (error) {
          console.error(
            `Failed to create lazy component for modal ${modal}:`,
            error
          );
        }
      }
    });
  }, []);

  useEffect(() => {
    preloadModals(modals);
  }, [modals, preloadModals]);

  return cacheRef.current;
};
